import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FiCamera } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Form, FormikProvider, useFormik } from 'formik';

import Avatar from '~/components/Avatar';
import Button from '~/components/Button';
import Input from '~/components/Formik/Input';
import TabsContainer, { ITab } from '~/components/TabsContainer';
import { useAuth } from '~/hooks/auth';
import { useFiles } from '~/hooks/files';
import { IState } from '~/store';
import { clearCepRequest, getCepRequest } from '~/store/modules/cep/actions';
import { updateUserProfileRequest } from '~/store/modules/users/actions';

import EditProfileValidationSchema from './EditProfileValidationSchema';
import {
  Box,
  InputList,
  CepBox,
  ButtonBox,
  Content,
  ImageContainer,
  OverlayBox,
} from './styles';

interface EditProfileFormikProps {
  setOpenModal: Dispatch<SetStateAction<boolean>>;
}

interface AvatarFile extends Blob {
  lastModified: number;
  lastModifiedDate: Date;
  name: string;
  size: number;
  type: string;
  webkitRelativePath: string;
}

const EditProfileFormik = ({ setOpenModal }: EditProfileFormikProps) => {
  const [tab, setTab] = useState<number>(0);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [avatarData, setAvatarData] = useState<AvatarFile | undefined>();

  const inputRef = useRef<HTMLInputElement>(null);

  const formTableRef = useRef(null);
  const userProfile = useSelector((state: IState) => state.users.userProfile);
  const cep = useSelector((state: IState) => state.cep.cep);
  const dynamicAttributes = userProfile?.dynamicAttributes;

  const { user } = useAuth();

  const { handleUploadThumbnail } = useFiles();

  const dispatch = useDispatch();

  const handleChange = (event: any) => {
    const { value } = event.target;
    if (value && value.replace(/[^\d]+/g, '').length >= 8) {
      dispatch(getCepRequest(value.replace(/[^\d]+/g, '')));
    }
  };

  const initialValues = {
    fullName: `${userProfile?.firstName} ${userProfile?.lastName}`,
    email: userProfile?.email,
    emailContact: dynamicAttributes?.emailContact,
    registration: userProfile?.registration,
    cpf: userProfile?.cpf,
    phone1: dynamicAttributes?.phone1,
    phone2: dynamicAttributes?.phone2,
    area: dynamicAttributes?.area,
    office: dynamicAttributes?.office,
    subArea: dynamicAttributes?.subArea,
    manager: dynamicAttributes?.manager,
    zipCode: dynamicAttributes?.zipCode,
    street: dynamicAttributes?.street,
    number: dynamicAttributes?.number,
    district: dynamicAttributes?.district,
    city: dynamicAttributes?.city,
    state: dynamicAttributes?.state,
    complement: dynamicAttributes?.complement,
    thumbnail: avatarData && avatarData.name,
  };

  useEffect(() => {
    if (cep) {
      formik.setValues({
        ...initialValues,
        zipCode: cep.cep,
        street: cep.street,
        district: cep.neighborhood,
        city: cep.city,
        state: cep.state,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cep]);

  useEffect(() => {
    return () => {
      dispatch(clearCepRequest());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (data: any) => {
    const [firstName, ...lastNameArray] = data.fullName.split(' ');
    const lastName = lastNameArray.join(' ');

    const keyFileName = `${new Date().getTime()}-${avatarData?.name}`;

    const key = avatarData ? keyFileName : userProfile?.thumbnail;

    const person = {
      firstName,
      lastName,
      cpf: data.cpf,
      dynamicAttributes: {
        area: data.area,
        zipCode: data.zipCode,
        street: data.street,
        number: data.number,
        district: data.district,
        city: data.city,
        state: data.state,
        complement: data.complement,
        phone1: data.phone1,
        phone2: data.phone2,
        subArea: data.subArea,
        emailContact: data.emailContact,
        manager: data.manager,
        office: data.office,
      },
      email: data.email,
      registration: data.registration,
      thumbnail: key,
    };

    if (avatarData) {
      handleUploadThumbnail(avatarData, key, (progress) => {
        if (progress === 100) {
          dispatch(updateUserProfileRequest(user.personId, person));
          setOpenModal(false);
        }
      });
    } else {
      dispatch(updateUserProfileRequest(user.personId, person));
      setOpenModal(false);
    }
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema: EditProfileValidationSchema,
  });

  const formikValues = formik.values;

  const handleAvatar = useCallback(async (e) => {
    const files = e.target.files[0];

    if (files.type !== 'image/jpeg' && files.type !== 'image/png') {
      toast.error('O avatar deve estar no formato PNG ou JPEG');
      return;
    }

    const url = await handleFileUrl(files);

    setAvatarPreview(url);
    setAvatarData(files);
  }, []);

  const handleFileUrl = (file: AvatarFile): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();

      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result as string);
      };

      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const requestTabs: ITab[] = [
    {
      label: 'Informações gerais',
      value: 0,
      content: (
        <>
          <Box>
            <Content
              onChange={handleAvatar}
              onClick={() => inputRef.current?.click()}
            >
              <ImageContainer>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="thumbnail" />
                ) : (
                  <Avatar
                    variant="secondary"
                    image={userProfile?.thumbnail}
                    size={80}
                  />
                )}
              </ImageContainer>
              <OverlayBox>
                <FiCamera size={20} />
                <p>Alterar foto</p>
              </OverlayBox>

              <input
                type="file"
                name="thumbnail"
                style={{ display: 'none' }}
                onChange={formik.handleChange}
                ref={inputRef}
              />
            </Content>

            <Input
              name="fullName"
              label="Nome completo"
              placeholder="insira o nome completo"
              value={formikValues.fullName}
            />
          </Box>
          <InputList>
            <Input
              name="email"
              label="E-mail de cadastro"
              placeholder="insira o e-mail de cadastro"
              disabled
            />
            <Input
              name="emailContact"
              label="E-mail de contato"
              placeholder="insira o e-mail de contato"
              value={formikValues?.emailContact}
            />
            <Input
              name="registration"
              label="Número de matrícula"
              placeholder="insira o número de matrícula"
              disabled
            />
            <Input
              name="cpf"
              label="CPF"
              placeholder="insira o CPF"
              value={formikValues?.cpf}
              mask="999.999.999-99"
            />
            <Input
              name="phone1"
              label="Telefone 1"
              placeholder="insira o número do telefone 1"
              value={formikValues?.phone1}
              mask="(99) 99999-9999"
              optional
            />
            <Input
              name="phone2"
              label="Telefone 2"
              placeholder="insira o número do telefone 2"
              value={formikValues?.phone2}
              mask="(99) 99999-9999"
              optional
            />
            <Input
              name="area"
              label="Área"
              placeholder="insira a área"
              disabled
            />
            <Input
              name="subArea"
              label="Sub área"
              placeholder="insira a sub área"
              value={formikValues?.subArea}
              optional
            />
            <Input
              name="office"
              label="Cargo"
              placeholder="insira o cargo"
              disabled
            />
            <Input
              name="manager"
              label="Gestor"
              placeholder="insira o nome do gestor"
              value={formikValues?.manager}
              optional
            />
          </InputList>
        </>
      ),
    },
    {
      label: 'Endereço',
      value: 1,
      content: (
        <>
          <CepBox>
            <Input
              name="zipCode"
              label="CEP"
              placeholder="insira o CEP"
              value={formikValues?.zipCode}
              onChange={handleChange}
              mask="99999-999"
            />
          </CepBox>
          <InputList>
            <Input
              name="street"
              label="Logradouro"
              placeholder="insira o logradouro"
              value={formikValues?.street}
            />
            <Input
              name="number"
              label="Número"
              placeholder="insira o número"
              value={formikValues?.number}
            />
            <Input
              name="district"
              label="Bairro"
              placeholder="insira o bairro"
              value={formikValues?.district}
            />
            <Input
              name="complement"
              label="Complemento"
              optional
              placeholder="insira o complemento"
              value={formikValues?.complement}
            />
            <Input
              name="city"
              label="Cidade"
              placeholder="insira a cidade"
              value={formikValues?.city}
            />
            <Input
              name="state"
              label="UF"
              placeholder="insira o UF"
              value={formikValues?.state}
            />
          </InputList>
        </>
      ),
    },
  ];

  return (
    <FormikProvider value={formik}>
      <Form ref={formTableRef} onSubmit={formik.handleSubmit}>
        <TabsContainer
          ariaLabel="request_tabs"
          value={tab}
          onChange={setTab}
          options={requestTabs}
        />

        <ButtonBox>
          <Button
            disabled={formik.errors && Object.keys(formik.errors).length > 0}
            type="submit"
          >
            salvar
          </Button>
        </ButtonBox>
      </Form>
    </FormikProvider>
  );
};

export default EditProfileFormik;
